/**
 * Custom hook for managing API keys
 */

import { useCallback, useState } from 'react';

import { fetchWithDefaults } from '@/lib/http/client';

interface UseAPIKeysOptions {
  onKeysChange?: () => void | Promise<void>;
}

export function useAPIKeys(options: UseAPIKeysOptions = {}) {
  const { onKeysChange } = options;

  const [newApiKey, setNewApiKey] = useState<string | null>(null);
  const [showNewKeyModal, setShowNewKeyModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createAPIKey = useCallback(async () => {
    try {
      setCreating(true);
      setError(null);

      const response = await fetchWithDefaults('/users/api/api-keys/create/', {
        method: 'POST',
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create API key');
      }

      const result = await response.json();
      setNewApiKey(result.key);
      setShowNewKeyModal(true);

      // Trigger refresh callback
      if (onKeysChange) {
        await onKeysChange();
      }
    } catch (err) {
      console.error('Error creating API key:', err);
      setError("We couldn't create your API key. Try again.");
      throw err;
    } finally {
      setCreating(false);
    }
  }, [onKeysChange]);

  const revokeAPIKey = useCallback(
    async (keyId: number, confirm: boolean = true) => {
      if (
        confirm &&
        !window.confirm(
          'Are you sure you want to revoke this API key? This action cannot be undone.'
        )
      ) {
        return;
      }

      try {
        setRevoking(true);
        setError(null);

        const response = await fetchWithDefaults(
          '/users/api/api-keys/revoke/',
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ key_id: keyId }),
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to revoke API key');
        }

        // Trigger refresh callback
        if (onKeysChange) {
          await onKeysChange();
        }
      } catch (err) {
        console.error('Error revoking API key:', err);
        setError("We couldn't revoke that API key. Try again.");
        throw err;
      } finally {
        setRevoking(false);
      }
    },
    [onKeysChange]
  );

  const clearNewKey = useCallback(() => {
    setNewApiKey(null);
    setShowNewKeyModal(false);
  }, []);

  const copyNewKey = useCallback(() => {
    if (newApiKey) {
      navigator.clipboard.writeText(newApiKey);
    }
  }, [newApiKey]);

  return {
    // State
    newApiKey,
    showNewKeyModal,
    creating,
    revoking,
    error,

    // Actions
    createAPIKey,
    revokeAPIKey,
    clearNewKey,
    copyNewKey,
  };
}
